import { useEffect, useRef, useState } from "react";
import { MAX_CHAT_IMAGES, useChatImages } from "@/core/components/chat-images";

/**
 * The screenshots in the composer, and where a saved one is read: the chat's
 * `…/images/:id`. Only chats that accept images show them. Another chat
 * (another report) starts with none.
 */
export const useComposerImages = (chatPath: string) => {
  const [images, setImages] = useState<File[]>([]);
  const chatImages = useChatImages([images, setImages]);
  // Bumped each time the composer is emptied, so a send knows whether the
  // composer it emptied is still the one on screen.
  const generation = useRef(0);
  const clearImages = () => {
    generation.current += 1;
    setImages([]);
  };
  useEffect(() => {
    generation.current += 1;
    setImages([]);
  }, [chatPath]);
  return {
    chatImages,
    clearImages,
    /**
     * Empties the composer for a send. The returned `restore` puts the
     * screenshots back, in front of any added since, when the API refused
     * the message, but only while the composer has not been emptied again
     * (another conversation opened, or another message sent).
     */
    takeImages: () => {
      const sent = images;
      clearImages();
      const taken = generation.current;
      return () => {
        if (generation.current !== taken) return;
        setImages((current) =>
          [...sent, ...current.filter((image) => !sent.includes(image))].slice(
            0,
            MAX_CHAT_IMAGES,
          ),
        );
      };
    },
    imagePath: (imageId: string) => `${chatPath}/images/${imageId}`,
  };
};
